import { call, put, select, takeEvery } from 'redux-saga/effects'

import { IAgentSourcedCreatorMeta } from '@components/CreatorSearch/types'
import { IHateoasLink } from '@store'
import {
  fetchAgentSourcedCreatorsCount,
  fetchAgentSourcedCreatorsCountFailure,
  fetchAgentSourcedCreatorsCountSuccess,
} from '@store/agentSourcedCreators/actions'
import { selectAuthToken } from '@store/auth/selectors'
import { selectCampaignLink } from '@store/campaigns'
import { selectAgentSourcedCreatorsLink } from '@store/me'
import { networkRequest } from '@utils/networkRequest'

const VIBE_CHECK_LIST_REL = 'agent_sourced_creators'
const INTERESTED_REL = 'interested_candidates'
const SHORTLISTED_REL = 'shortlisted_candidates'

interface ICountResponse {
  metadata: IAgentSourcedCreatorMeta | null
}

export function* fetchCountSaga(action: ReturnType<typeof fetchAgentSourcedCreatorsCount>) {
  try {
    const { campaignId, listing } = action.payload
    const token: string = yield select(selectAuthToken)

    let href: string
    let method: string

    if (listing === 'interested' || listing === 'shortlisted') {
      // Callers gate dispatch on isCampaignFetched, so the campaign is in
      // store by the time we reach here. Link absent ⇒ vibe check not
      // complete yet ⇒ fail fast (silently to the UI count badge).
      const rel = listing === 'shortlisted' ? SHORTLISTED_REL : INTERESTED_REL
      const link: IHateoasLink | undefined = yield select(selectCampaignLink, campaignId, rel)
      if (!link) {
        yield put(
          fetchAgentSourcedCreatorsCountFailure(
            `HATEOAS link '${rel}' not found on campaign ${campaignId}; vibe check may not be complete yet.`,
          ),
        )
        return
      }
      const url = new URL(link.href)
      url.searchParams.set('per_page', '1')
      href = url.toString()
      method = link.method
    } else {
      // vibe-check: /me-level template
      const link: IHateoasLink | undefined = yield select(selectAgentSourcedCreatorsLink)
      if (!link) {
        yield put(
          fetchAgentSourcedCreatorsCountFailure(
            `HATEOAS link '${VIBE_CHECK_LIST_REL}' not found on /me`,
          ),
        )
        return
      }
      const collectionHref = link.href
        .replace('{brief_id}', String(campaignId))
        .replace('/{agent_sourced_creator_id}', '')
      const url = new URL(collectionHref)
      url.searchParams.set('per_page', '1')
      href = url.toString()
      method = link.method
    }

    const response: ICountResponse = yield call(networkRequest, method, href, undefined, token)
    const total = response.metadata?.total ?? 0
    // BRAND-9678: forward `metadata.stats` too. All three of these endpoints
    // compute the same unfiltered bucket_stats block on their first page, and
    // a `per_page=1` warm-up is a first page — so the interested/shortlisted
    // warm-ups that already run on every brief open carry the correct
    // ALL-RUNS vibe-check figure with them. `metadata.total` stays the
    // per-listing fallback for interested/shortlisted only — on a vibe-check
    // response it is scoped to the selected run, so the reducer keeps
    // ignoring it (see fetchAgentSourcedCreatorsCountSuccess).
    const stats = response.metadata?.stats

    yield put(fetchAgentSourcedCreatorsCountSuccess({ listing, total, campaignId, stats }))
  } catch (e: any) {
    yield put(fetchAgentSourcedCreatorsCountFailure(e?.message ?? 'Failed to load creator count'))
  }
}

// takeEvery, not takeLatest, is load-bearing: Filters dispatches up to three
// warm-ups (vibe-check/interested/shortlisted) in one effect and each must
// complete — takeLatest would leave only the last listing's count. The
// reducer therefore does the discarding, per BRIEF rather than per request;
// see fetchAgentSourcedCreatorsCountSuccess for the residual that leaves.
export default function* watchFetchAgentSourcedCreatorsCount() {
  yield takeEvery(fetchAgentSourcedCreatorsCount.type, fetchCountSaga)
}
